/* eslint-disable max-statements */
import { Badge, Box, Flex, Loader } from '@mantine/core';
import ChartsEmbedSDK from '@mongodb-js/charts-embed-dom';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Chart } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { Table, TableActions, TableColumnProps } from '@components';
import { useChartsAuth, useRetrieveReports } from '@hooks/chart';
import { downloadChartDataAsExcel } from './helper';
import classes from './MongoDashboard.module.css';

type SDKChartDataFields = Record<string, string>;
interface SDKChartData {
  fields: SDKChartDataFields;
  documents: SDKChartDataFields[];
}

interface ChartStatus {
  status: 'loading' | 'ready' | 'error';
  sdkChart?: any;
}

export const ReportsTab = () => {
  const { data: reportList, isLoading: isChartsLoading } = useRetrieveReports();
  const { data: chartAuth, isLoading: isTokenLoading } = useChartsAuth();
  const token = chartAuth?.token;
  const chartsBaseUrl = chartAuth?.chartsBaseUrl;
  const [chartStatuses, setChartStatuses] = useState<Record<string, ChartStatus>>({});
  const hiddenContainerRef = useRef<HTMLDivElement>(null);

  const initializeChart = async (chart: Chart, token: string) => {
    if (!token || !chartsBaseUrl || !hiddenContainerRef.current) return;

    setChartStatuses((prev) => ({
      ...prev,
      [chart.chartId]: {
        status: 'loading',
      },
    }));

    try {
      const sdk = new ChartsEmbedSDK({
        baseUrl: chartsBaseUrl,
        getUserToken: () => token,
      });

      const sdkChart = sdk.createChart({
        chartId: chart.chartId,
        height: '0px',
      });

      const existingDiv = document.getElementById(`hidden-chart-${chart.chartId}`);
      if (existingDiv) {
        existingDiv.remove();
      }

      const tempDiv = document.createElement('div');
      tempDiv.id = `hidden-chart-${chart.chartId}`;
      tempDiv.style.position = 'absolute';
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.height = '0px';
      tempDiv.style.overflow = 'hidden';

      hiddenContainerRef.current.appendChild(tempDiv);
      await sdkChart.render(tempDiv);

      setChartStatuses((prev) => ({
        ...prev,
        [chart.chartId]: {
          status: 'ready',
          sdkChart,
        },
      }));
    } catch {
      setChartStatuses((prev) => ({
        ...prev,
        [chart.chartId]: {
          status: 'error',
        },
      }));
    }
  };

  const initializeCharts = async () => {
    const initialStatuses: Record<string, ChartStatus> = {};
    reportList.forEach((chart) => {
      initialStatuses[chart.chartId] = {
        status: 'loading',
      };
    });
    setChartStatuses(initialStatuses);

    await Promise.all(reportList.map((chart) => initializeChart(chart as Chart, token)));
  };

  useEffect(() => {
    if (!reportList || !token || !chartsBaseUrl || isChartsLoading || isTokenLoading) {
      return;
    }

    initializeCharts();

    return () => {
      if (hiddenContainerRef.current) {
        hiddenContainerRef.current.innerHTML = '';
      }
    };
  }, [reportList, token, isChartsLoading, isTokenLoading, chartsBaseUrl]);

  const handleDownloadReport = async (chart: Chart) => {
    try {
      const data = (await chartStatuses[chart.chartId].sdkChart.getData()) as unknown as SDKChartData;
      downloadChartDataAsExcel(data, chart.name);
      toast.success('Report downloaded successfully');
    } catch {
      toast.error('Failed to download report');
    }
  };

  const handleRefreshChart = async (chart: Chart) => {
    if (!token) return;

    try {
      await initializeChart(chart, token);
      toast.success('Chart refreshed successfully');
    } catch {
      toast.error('Failed to refresh chart');
    }
  };

  const getStatusBadge = (chartId: string) => {
    const status = chartStatuses[chartId]?.status || 'loading';
    let color = 'red';
    let text = 'Error';

    switch (status) {
      case 'loading': {
        color = 'yellow';
        text = 'Loading...';
        break;
      }
      case 'ready': {
        color = 'green';
        text = 'Ready to Download';
        break;
      }
      default:
        break;
    }

    return (
      <Badge color={color} variant="filled">
        {text}
      </Badge>
    );
  };

  const columns: TableColumnProps<Chart>[] = [
    {
      name: 'Report Name',
      selector: 'name',
      cell: (row) => row.name,
      sortable: false,
    },
    {
      name: 'Status',
      selector: 'status',
      cell: (row) => getStatusBadge(row.chartId),
      sortable: false,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => {
        const actions = [
          {
            icon: IconFactory.refresh,
            text: 'Refresh Report',
            onClick: () => handleRefreshChart(row),
          },
        ];

        if (chartStatuses[row.chartId]?.status === 'ready') {
          actions.push({
            icon: IconFactory.download,
            text: 'Download Report',
            onClick: () => handleDownloadReport(row),
          });
        }

        return <TableActions actions={actions} />;
      },
    },
  ];

  return (
    <>
      <div ref={hiddenContainerRef} style={{ display: 'none' }} />

      {(isChartsLoading || isTokenLoading) && (
        <Flex className={classes.loadingAndNoDataContainer}>
          <Loader />
        </Flex>
      )}
      <Box pl={20} pr={20} pt={10} pb={10}>
        {!isChartsLoading && !isTokenLoading && (
          <Table columns={columns} dataSource={reportList as Chart[]} isLoading={isChartsLoading} pagination total={reportList.length} />
        )}
      </Box>
    </>
  );
};
